// Lazy-initialized emoji search shared by the composer's ":shortcode:"
// autocomplete and the emoji picker button. The emoji-mart dataset is only
// loaded (as an async chunk) the first time an emoji feature is actually used,
// so the main bundle does not grow.

let indexPromise = null;

const ensureSearchIndex = () => {
  if (!indexPromise) {
    indexPromise = Promise.all([import('emoji-mart'), import('@emoji-mart/data')]).then(
      async ([emojiMart, dataModule]) => {
        await emojiMart.init({ data: dataModule.default });
        return emojiMart.SearchIndex;
      }
    );
  }
  return indexPromise;
};

const toSuggestion = emoji => ({
  id: emoji.id,
  native: emoji.skins?.[0]?.native || '',
  name: emoji.name || emoji.id,
});

// Returns up to `max` emoji suggestions for a shortcode fragment (":clo" → "clo").
export const searchEmojis = async (query, max = 8) => {
  const q = String(query || '').trim();
  if (!q) return [];
  try {
    const SearchIndex = await ensureSearchIndex();
    const results = (await SearchIndex.search(q)) || [];
    // Prefer shortcode (id) prefix matches — that is what the user is typing —
    // then keyword matches, preserving emoji-mart's relevance order within each.
    const prefix = [];
    const rest = [];
    for (const emoji of results) {
      if (!emoji?.skins?.[0]?.native) continue;
      if (String(emoji.id).startsWith(q.toLowerCase())) prefix.push(emoji);
      else rest.push(emoji);
    }
    return [...prefix, ...rest].slice(0, max).map(toSuggestion);
  } catch (_e) {
    return [];
  }
};

// Resolves a complete shortcode (":cloud:" → "cloud") to its emoji, or null.
export const findEmojiByShortcode = async shortcode => {
  const q = String(shortcode || '').trim().toLowerCase();
  if (!q) return null;
  try {
    const SearchIndex = await ensureSearchIndex();
    const results = (await SearchIndex.search(q)) || [];
    const exact = results.find(e => String(e.id).toLowerCase() === q && e?.skins?.[0]?.native);
    return exact ? toSuggestion(exact) : null;
  } catch (_e) {
    return null;
  }
};

// Matches an in-progress shortcode directly before the caret, e.g. ":clo".
// The colon must be at the start of the text or preceded by whitespace so
// times like "13:45" never trigger the autocomplete.
export const matchShortcodeQuery = (value, caret) => {
  const before = String(value || '').slice(0, caret);
  const match = before.match(/(?:^|[\s\n])(:([a-z0-9_+-]{2,}))$/i);
  if (!match) return null;
  return { query: match[2], start: caret - match[1].length, end: caret };
};

// Matches a just-completed shortcode (closing colon typed) before the caret.
export const matchCompletedShortcode = (value, caret) => {
  const before = String(value || '').slice(0, caret);
  const match = before.match(/(?:^|[\s\n])(:([a-z0-9_+-]{2,}):)$/i);
  if (!match) return null;
  return { shortcode: match[2], start: caret - match[1].length, end: caret };
};
